import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDateTime, relativeTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Connection = Schema<'LinkedInConnectionOut'>
type Post = Schema<'PostOut'>
type PostState = Schema<'PostState'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * `PublishRequest.commentary`'s ceiling in `api/app/v1/connections.py`, which is
 * LinkedIn's own. Mirrored rather than derived because OpenAPI describes the
 * bound and the generated client does not enforce it — and a draft that only
 * discovers the limit as a 422 has spent the tenant's typing, not their budget.
 */
const MAX_CHARACTERS = 3000

/** `DEFAULT_POSTS` and `MAX_POSTS` in `api/app/db/linkedin.py`, mirrored. */
const FIRST_PAGE = 50
const MAX_PAGE = 200

/**
 * Publish to LinkedIn — `design/screens/outreach.html#post`, audit row §A5,
 * over `POST` and `GET /api/v1/connections/linkedin/posts`.
 *
 * S-B17 shipped the queue, the cap, the pacing floor and the publisher on
 * 2026-08-05, and the only way to use any of it was an HTTP client. This is the
 * screen. **The `GET` did not exist before S-G9** and is part of this row rather
 * than a nicety: the endpoint queues a post and answers with a job id, so
 * without a list there is no way for the person who pressed the button to learn
 * what happened to it. `api/app/v1/connections.py` argues that at the route.
 *
 * WHY THERE IS NO SIDEBAR ENTRY
 * -------------------------------
 * S-D3 approved seven Outreach sections and *Publish to LinkedIn* is not one of
 * them, so it is a `SUBSCREENS` entry in `web/src/shell/navigation.ts` — the
 * same call S-G5 made for `/seo/publish-targets`, and for the same reason:
 * adding an eighth section is inventing information architecture nobody drew.
 * It is reached from the LinkedIn card in Settings → Connections, which is where
 * a tenant goes when they are thinking about their LinkedIn account.
 *
 * WHAT THE PROTOTYPE DRAWS AND THIS SCREEN DELIBERATELY DOES NOT
 * ---------------------------------------------------------------
 * Three controls, and each is absent for a different reason.
 *
 * **Save draft.** There is no draft. A post exists in exactly two places — a
 * textarea in this browser, and `public.jobs` once it is queued — and there is
 * no endpoint, table or column between them. Rendering the button would be
 * rule 1's *"we'll wire this up later"* with a nicer label.
 *
 * **When: Now / Tomorrow 10:30 / Pick a time.** Nothing schedules. `run_after`
 * on the queued job is computed by the pacing floor and deferred by the send
 * window (C1) — the *server* decides when a post goes out, and it will not
 * accept a time from the client. So the screen shows what the API actually
 * answers, which is the earliest moment the post may be attempted, and says
 * plainly that AugRank spaces them. That is more information than the prototype's
 * dropdown, not less; it is just not a control.
 *
 * **Reaction counts on the recent-posts list.** AugRank never reads them. The
 * publisher is a one-way `POST` to LinkedIn's Posts API and nothing polls the
 * share afterwards, so *41 reactions* would be a number this product made up.
 * This is the fourth §A row to meet the same rule — §A3's *30-day uptime*, §V2's
 * ₹4.20, §A4's *218 suppressed* — and the answer is the same one: show the fact
 * that exists, labelled as what it is.
 *
 * WHY THE LIST IS RENDERED FOR A MEMBER AND THE COMPOSER IS NOT
 * --------------------------------------------------------------
 * `GET` is `tenant:read` and `POST` is `discovery:manage`, which is the split
 * the server draws and the reason it draws it: a member needs to know whether
 * the organisation's posts are going out — that is why they came here — without
 * being able to put something on the company's LinkedIn account. A screen that
 * refused a member outright would hide a read they are entitled to; one that
 * showed them a composer would hand them a 403 on the press.
 */
export function PublishPost(): JSX.Element {
  const { role } = useActiveTenant()
  const mayPublish = may(role, 'discovery:manage')
  const [limit, setLimit] = useState(FIRST_PAGE)

  const connection = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/linkedin', { signal })),
    [],
  )

  const posts = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/connections/linkedin/posts', {
          params: { query: { limit } },
          signal,
        }),
      ),
    [limit],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Publish to LinkedIn</h1>
        <span className="app-spacer" />
        <Allowance connection={connection.state.data ?? null} />
      </div>

      <div className="app-post">
        <div className="ar-stack">
          {connection.state.status === 'loading' ? (
            <Loading label="Loading your LinkedIn connection" lines={5} />
          ) : connection.state.status === 'error' ? (
            <div className="ar-card">
              <div className="ar-card__body">
                <ErrorState failure={connection.state.failure} onRetry={connection.refetch} />
              </div>
            </div>
          ) : !mayPublish ? (
            <NotYours />
          ) : (
            <Composer
              connection={connection.state.data}
              onQueued={() => {
                connection.refetch()
                posts.refetch()
              }}
            />
          )}
        </div>

        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <h3>Recent posts</h3>
            </div>
            <RecentPosts
              posts={posts}
              connection={connection.state.data ?? null}
              limit={limit}
              onShowMore={() => {
                setLimit(MAX_PAGE)
              }}
            />
          </div>

          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>Publishing is not scraping</strong>
              This uses LinkedIn&rsquo;s own API with your consent, so it carries none of the
              account risk the search side does. It still counts against the daily cap, because a
              burst of posts looks automated whichever door it came through.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* --- the day's allowance --------------------------------------------------------- */

/**
 * *18 of 20 posts used today* — and it is the counter the account-health screen
 * shows, not a second one.
 *
 * `posts_today` and `max_posts_per_day` both come from `GET
 * /connections/linkedin`, which counts `public.jobs` rather than holding a
 * column, so this badge and the health screen's allowance cannot drift apart.
 * Nothing is rendered until the connection has loaded: a badge that said *0 of
 * 20* for half a second and then corrected itself would be worse than a gap.
 */
function Allowance({ connection }: { connection: Connection | null }): JSX.Element | null {
  if (connection === null) return null
  const { posts_today: used, max_posts_per_day: cap, posts_remaining_today: left } = connection
  const tone = left === 0 ? 'danger' : left <= 2 ? 'warning' : 'neutral'

  // One string rather than three children: a badge assembled from several text
  // nodes cannot be found by the text it visibly says, which is how it reads to
  // a screen reader too.
  const mark = left === 0 ? '✕ ' : left <= 2 ? '⚠ ' : ''

  return (
    <span className={`ar-badge ar-badge--${tone}`}>
      {`${mark}${String(used)} of ${String(cap)} posts used today`}
    </span>
  )
}

/* --- what a member sees ---------------------------------------------------------- */

function NotYours(): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <Empty
          title="Only owners and admins can publish"
          body="Posting puts something on your organisation's LinkedIn account in its name, so it is restricted to the roles that can also connect and disconnect it. What has been published is listed beside this."
        />
      </div>
    </div>
  )
}

/* --- composing ------------------------------------------------------------------- */

/**
 * The compose card. One textarea, one author, and a button that **queues**.
 *
 * WHY THE IDEMPOTENCY KEY IS A FUNCTION OF THE DRAFT
 * ----------------------------------------------------
 * `Idempotency-Key` is required by the route, and the reason it gives is that
 * LinkedIn has no idempotency of its own and no API to delete a post: a retried
 * request AugRank could not recognise is a second post on somebody's profile.
 * So the key has to be stable across a retry of *the same* post and different
 * for a *different* one, and the rule that gets both is that **an edit starts a
 * new attempt**. Typing regenerates it; pressing Publish again after a failure
 * does not. A key held for the life of the component would make the second post
 * of the day a silent no-op; a key generated at submit time would make a
 * dropped connection a double post.
 *
 * `created: false` is the case that proves it works — the same key arriving
 * twice returns the job already queued under it, and the screen says so rather
 * than claiming a second post went out.
 */
function Composer({
  connection,
  onQueued,
}: {
  connection: Connection
  onQueued: () => void
}): JSX.Element {
  const toast = useToast()
  const [commentary, setCommentary] = useState('')
  const [author, setAuthor] = useState('')
  const [key, setKey] = useState(newKey)

  const publish = useMutation((body: Schema<'PublishRequest'>) =>
    call(() =>
      api.POST('/api/v1/connections/linkedin/posts', {
        params: { header: { 'Idempotency-Key': key } },
        body,
      }),
    ),
  )

  if (!connection.connected) return <NotConnected connection={connection} />

  const characters = commentary.trim().length
  const spent = connection.posts_remaining_today === 0
  // `pages` is optional on the generated type — it has a `default_factory` on
  // the server, so OpenAPI marks it not-required — and the composer branches on
  // its length. Narrowed once here rather than at each of the two uses.
  const pages = connection.pages ?? []

  function edited(): void {
    // A different draft is a different post. See the docstring.
    setKey(newKey())
    publish.reset()
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await publish.run({
      commentary: commentary.trim(),
      // `null` rather than `undefined`: the generated types are checked with
      // `exactOptionalPropertyTypes`, and the server reads null as "my own
      // profile" — which is what an empty selection means.
      page_urn: author || null,
    })
    if (!result.ok) return

    setCommentary('')
    setKey(newKey())
    toast.show({
      kind: result.data.created ? 'success' : 'info',
      title: result.data.created ? 'Queued to go out' : 'That post was already queued',
      body: result.data.created
        ? `AugRank will publish it from ${formatDateTime(result.data.run_after, IST)}. Posts are spaced through the day.`
        : 'Nothing was published twice — the post you sent is already on its way.',
    })
    onQueued()
  }

  return (
    <form
      className="ar-card"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__header">
        <h3>New post</h3>
      </div>
      <div className="ar-card__body ar-stack">
        <InlineError failure={publish.state.failure} />

        {pages.length > 0 ? (
          <Field
            label="Post as"
            help="Both are granted by the same authorisation. Disconnecting LinkedIn in Settings removes both."
          >
            {(aria) => (
              <select
                {...aria}
                className="ar-select"
                value={author}
                onChange={(event) => {
                  setAuthor(event.target.value)
                  edited()
                }}
              >
                <option value="">
                  {connection.account_label ?? 'Your profile'} — personal profile
                </option>
                {pages.map((page) => (
                  <option key={page.urn} value={page.urn}>
                    {page.name} — company page
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <p className="ar-text-body-sm ar-muted">
            Publishing as <strong>{connection.account_label ?? 'your LinkedIn account'}</strong>.
            This authorisation covers the profile only — a company Page needs LinkedIn to grant
            AugRank the Pages permission, which it does per application rather than per account.
          </p>
        )}

        <Field
          label="Post"
          required
          help={`${String(characters)} of ${String(MAX_CHARACTERS)} characters. LinkedIn's own limit.`}
        >
          {(aria) => (
            <textarea
              {...aria}
              className="ar-textarea app-post__body"
              value={commentary}
              maxLength={MAX_CHARACTERS}
              placeholder="Finished a 3BHK in Gachibowli this week — full modular kitchen, wardrobes and false ceilings in nine weeks."
              onChange={(event) => {
                setCommentary(event.currentTarget.value)
                edited()
              }}
            />
          )}
        </Field>

        <p className="ar-text-body-sm ar-muted">
          Publishing queues the post rather than sending it this second. AugRank spaces posts
          through the day and holds them inside your send window, because{' '}
          {connection.max_posts_per_day} posts landing in a quarter of an hour is what makes an
          account look automated.
        </p>
      </div>
      <div className="ar-card__footer">
        <Button
          tone="primary"
          type="submit"
          busy={publish.pending}
          busyLabel="Queueing"
          disabled={characters === 0 || spent}
        >
          Publish
        </Button>
        <span className="ar-card__spacer" />
        <span className="ar-subtle ar-text-body-sm">
          {spent
            ? 'No posts left today. The allowance resets at midnight IST.'
            : `${String(connection.posts_remaining_today)} posts left today`}
        </span>
      </div>
    </form>
  )
}

/**
 * The state a tenant is actually in before they have connected anything — and
 * the one they land back in when a sixty-day grant runs out.
 *
 * Three sentences apart rather than one, because *never connected*, *LinkedIn
 * interrupted this account* and *the authorisation expired* have three different
 * remedies and only the middle one is not a button press.
 */
function NotConnected({ connection }: { connection: Connection }): JSX.Element {
  const body =
    connection.status === null
      ? 'AugRank publishes through LinkedIn’s own authorisation, which nobody has granted yet. Connecting takes one consent screen and about ten seconds.'
      : connection.paused
        ? 'LinkedIn interrupted this account. Clear whatever it is asking for in your own browser, then resume the account in Settings — AugRank never retries through a checkpoint.'
        : 'LinkedIn is no longer accepting this authorisation. It lasts sixty days and cannot be renewed in the background, so reconnecting is how it comes back.'

  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <Empty
          title="LinkedIn is not connected"
          body={body}
          action={
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/settings/connections">
              Go to Connections
            </Link>
          }
        />
        {connection.detail !== null ? (
          <p className="ar-text-body-sm ar-muted">LinkedIn said: {connection.detail}</p>
        ) : null}
      </div>
    </div>
  )
}

/* --- the recent-posts list -------------------------------------------------------- */

function RecentPosts({
  posts,
  connection,
  limit,
  onShowMore,
}: {
  posts: ReturnType<typeof useQuery<Schema<'PostListResponse'>>>
  connection: Connection | null
  limit: number
  onShowMore: () => void
}): JSX.Element {
  if (posts.state.status === 'loading') {
    return (
      <div className="ar-card__body">
        <Loading label="Loading your recent posts" lines={3} />
      </div>
    )
  }

  if (posts.state.status === 'error') {
    return (
      <div className="ar-card__body">
        <ErrorState failure={posts.state.failure} onRetry={posts.refetch} />
      </div>
    )
  }

  const rows = posts.state.data.posts

  if (rows.length === 0) {
    return (
      <div className="ar-card__body">
        <Empty
          title="Nothing posted yet"
          body="Everything you publish from here is listed with what became of it — queued, published, or failed and why. Write the first one beside this."
        />
      </div>
    )
  }

  // `LIMIT n` with no offset, so a full page is the only signal there may be
  // more — the same shape the suppression list uses, and it says why.
  const capped = rows.length >= limit

  return (
    <>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {rows.map((post) => (
          <PostPanel key={post.job_id} post={post} connection={connection} />
        ))}
      </div>
      {capped ? (
        <div className="ar-card__footer">
          {limit < MAX_PAGE ? (
            <Button size="sm" onClick={onShowMore}>
              Show more
            </Button>
          ) : (
            <span className="ar-subtle ar-text-body-sm">Showing the {MAX_PAGE} most recent.</span>
          )}
        </div>
      ) : null}
    </>
  )
}

/**
 * One post, and what became of it.
 *
 * The heading is the post's own first line. There is no title on a LinkedIn
 * post — the prototype invents *"Gachibowli 3BHK — 9 weeks"* — and the first
 * line is what a person actually recognises their own post by.
 */
function PostPanel({
  post,
  connection,
}: {
  post: Post
  connection: Connection | null
}): JSX.Element {
  const state = STATES[post.state]
  const now = new Date()
  // Pulled out of the JSX because the generated type is `string | null |
  // undefined` — the field is optional on the schema — and narrowing it inline
  // leaves a template literal taking a possibly-undefined argument.
  const detail = post.detail ?? null

  return (
    <div className="ar-panel">
      <strong className="app-post__excerpt">{firstLine(post.commentary)}</strong>
      <span className="ar-subtle app-post__meta">
        {authorLabel(post, connection)} · {when(post, now)}
      </span>
      {post.state !== 'published' ? (
        <span className={`ar-badge ar-badge--${state.tone} app-post__state`}>{state.label}</span>
      ) : null}
      {detail !== null ? (
        <span className="ar-subtle app-post__meta">
          {post.state === 'failed'
            ? detail
            : `Attempt ${String(post.attempts)} of ${String(post.max_attempts)} did not go through: ${detail}`}
        </span>
      ) : null}
      {post.state === 'failed' ? (
        <span className="ar-subtle app-post__meta">
          {post.reason === 'challenge'
            ? 'The account was paused by a LinkedIn checkpoint. Nothing is retried through one — clear it, resume the account, then post again.'
            : 'It was not published and will not be retried. Copy the text into a new post once whatever LinkedIn objected to is fixed.'}
        </span>
      ) : null}
    </div>
  )
}

/** `PostState`, in the tenant's words. The four the API answers. */
const STATES: Record<
  PostState,
  { tone: 'neutral' | 'info' | 'success' | 'danger'; label: string }
> = {
  queued: { tone: 'neutral', label: 'Queued' },
  publishing: { tone: 'info', label: 'Publishing now' },
  published: { tone: 'success', label: 'Published' },
  failed: { tone: 'danger', label: '✕ Failed' },
}

/**
 * The one time each state is actually about.
 *
 * A queued post's interesting moment is in the future and a published one's is
 * in the past, so a single column would be *"2 days ago"* for one and a lie for
 * the other. `run_after` is the earliest the queue may attempt it, which is what
 * the `POST` answered when the button was pressed.
 */
function when(post: Post, now: Date): string {
  if (post.published_at !== null && post.published_at !== undefined) {
    return `published ${relativeTime(post.published_at, now)}`
  }
  if (post.state === 'queued') return `goes out from ${formatDateTime(post.run_after, IST)}`
  if (post.state === 'publishing') return 'going out now'
  return `tried ${relativeTime(post.run_after, now)}`
}

/**
 * Whose account it went to, resolved against the connection's own Pages.
 *
 * The API returns the author's URN rather than a label, because the Pages are
 * already on the connection this screen reads — composing the label on the
 * server would be a second answer to the same question, free to disagree with
 * the picker in the composer.
 */
function authorLabel(post: Post, connection: Connection | null): string {
  if (post.author_kind !== 'organization') return 'Profile'
  const page = (connection?.pages ?? []).find((candidate) => candidate.urn === post.author_urn)
  return page === undefined ? 'Company page' : `${page.name} · company page`
}

/** The post's first non-empty line, trimmed to something a card can hold. */
function firstLine(commentary: string): string {
  const line = commentary.split('\n').find((candidate) => candidate.trim() !== '') ?? commentary
  const trimmed = line.trim()
  return trimmed.length > 80 ? `${trimmed.slice(0, 79)}…` : trimmed
}

/**
 * A fresh `Idempotency-Key`.
 *
 * `crypto.randomUUID` rather than a counter or a timestamp: two tabs open on
 * this screen must not produce the same key, and a timestamp at second
 * resolution can. It is 36 characters, inside the route's 8–120.
 */
function newKey(): string {
  return crypto.randomUUID()
}
